import React, { FunctionComponent, MouseEvent } from 'react';
import { Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Service from 'model/Service';
import clsx from 'clsx';
import { Status } from 'model';
import Button from 'components/UI/Button';
import { reduceResolution, wbp } from 'Theme';

interface Props {
    service: Service;
    onEdit: () => void;
    onAddStaff: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            height: '100%'
        },
        categoryNamePetTypeContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '30%',
            alignSelf: 'center',
            [theme.breakpoints.up('md')]: {
                marginRight: 35,
                marginLeft: 40
            },
            [theme.breakpoints.down('md')]: {
                width: '95%',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingLeft: 12
            },
            [theme.breakpoints.down('sm')]: {
                width: '89%'
            }
        },
        nameContainer: {
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('md')]: {
                width: '60%'
            }
        },
        name: {
            textAlign: 'left',
            fontSize: 15,
            fontWeight: 500,
            lineHeight: '100%'
        },
        petTypeContainer: {
            display: 'flex',
            marginTop: 10,
            marginBottom: 0,
            [theme.breakpoints.down('md')]: {
                justifyContent: 'flex-end',
                marginBottom: 10
            },
            [theme.breakpoints.down('sm')]: {
                marginTop: 0,
                marginBottom: 0,
                alignItems: 'center',
                marginLeft: 10
            }
        },
        petType: {
            height: 23,
            marginRight: 7,
            [theme.breakpoints.down('md')]: {
                height: 23
            }
        },
        durationContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '17%',
            [theme.breakpoints.up('md')]: {
                marginRight: 10
            }
        },
        staffContainer: {
            display: 'flex',
            width: '18%',
            alignSelf: 'center',
            [theme.breakpoints.up('md')]: {
                marginRight: 6
            },
            [theme.breakpoints.down('md')]: {
                display: 'none'
            }
        },
        priceContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '19%',
            [theme.breakpoints.up('md')]: {
                marginRight: 10
            }
        },
        price: {
            display: 'flex',
            width: '100%'
        },
        taxesContainer: {
            display: 'flex',
            alignItems: 'center',
            width: '10%'
        },
        cell: {
            fontWeight: 400,
            fontSize: 14,
            lineHeight: '100%'
        },
        inactive: {
            opacity: 0.4
        },
        button: {
            'color': theme.palette.primary.main,
            'borderColor': theme.palette.primary.main,
            [theme.breakpoints.down(wbp)]: {
                width: reduceResolution(164),
                height: reduceResolution(45),
                fontSize: reduceResolution(16)
            },
            [theme.breakpoints.up(wbp)]: {
                width: 164,
                height: 45,
                fontSize: 16
            },
            'fontWeight': 600,
            '&:hover': {
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main
            }
        },
        dogs: {
            'padding': '4px 10px',
            '&.MuiChip-outlined': {
                'color': '#FFF',
                'borderColor': '#5E8677',
                'backgroundColor': '#5E8677',
                'fontSize': 12,
                'fontWeight': 500,
                'height': 26,
                'padding': 0,
                'lineHeight': '24px',
                [theme.breakpoints.down('md')]: {
                    fontSize: 13,
                    lineHeight: '100%',
                    height: 21
                },
                '& .MuiChip-label': {
                    padding: '4px 10px'
                }
            }
        },
        cats: {
            'padding': '4px 10px',
            '&.MuiChip-outlined': {
                'color': '#000',
                'borderColor': '#F2D2A2',
                'backgroundColor': '#F2D2A2',
                'fontSize': 12,
                'fontWeight': 500,
                'height': 26,
                'padding': 0,
                [theme.breakpoints.down('md')]: {
                    fontSize: 13,
                    lineHeight: '100%',
                    height: 21
                },
                '& .MuiChip-label': {
                    padding: '4px 10px'
                }
            }
        },
        exotics: {
            'padding': '4px 10px',
            '&.MuiChip-outlined': {
                'color': '#9C8267',
                'borderColor': '#EBE7E3',
                'backgroundColor': '#EBE7E3',
                'fontSize': 12,
                'fontWeight': 500,
                'height': 26,
                'padding': 0,
                [theme.breakpoints.down('md')]: {
                    fontSize: 13,
                    lineHeight: '100%',
                    height: 21
                },
                '& .MuiChip-label': {
                    padding: '4px 10px'
                }
            }
        },
        hover: {
            background: '#fff !important'
        },
        hideOnMobile: {
            [theme.breakpoints.down('md')]: {
                display: 'none'
            }
        },
        color: {
            width: 6
        }
    })
);

export const ServicesRowContent: FunctionComponent<Props> = props => {
    const classes = useStyles(props);

    const addStaff = props.service.staff.length === 0;
    const buttonLabel = addStaff ? 'Add Staff' : 'Finish Setup';

    const getPetClass = (type: string) => {
        switch (type) {
            case 'Dogs':
                return classes.dogs;
            case 'Cats':
                return classes.cats;
            case 'Exotics':
                return classes.exotics;
            default:
                return '';
        }
    };

    const durationMessage = (hours: number, minutes: number) => {
        let mensaje = '';

        if (hours > 0) {
            if (hours === 1) {
                minutes === 0
                    ? (mensaje = hours + '  hour')
                    : minutes === 1
                    ? (mensaje = hours + '  hour ' + minutes + ' min')
                    : (mensaje = hours + '  hour ' + minutes + ' min');
            } else {
                minutes === 0
                    ? (mensaje = hours + '  hours')
                    : minutes === 1
                    ? (mensaje = hours + '  hours ' + minutes + ' min')
                    : (mensaje = hours + '  hours ' + minutes + ' min');
            }
        } else {
            minutes === 0
                ? (mensaje = '')
                : minutes === 1
                ? (mensaje = minutes + ' min')
                : (mensaje = minutes + ' min');
        }

        return mensaje;
    };

    const petTypes = props.service.petTypes.map(petType => (
        <Chip
            key={petType.id}
            label={petType.singular}
            variant="outlined"
            color={petType.plural === 'Cats' ? 'primary' : undefined}
            className={clsx(classes.petType, getPetClass(petType.plural))}
        />
    ));

    const containerStyle = clsx(classes.container, {
        [classes.inactive]: props.service.status.id === Status.INACTIVE
    });

    const clickButtonHandler = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (addStaff) {
            props.onAddStaff();
        } else {
            props.onEdit();
        }
    };

    const taxes = <Typography className={classes.cell}>{props.service.pricing.taxes.length + ' Taxes'}</Typography>;
    const noTaxes = <Typography className={classes.cell}>No taxable</Typography>;
    const fixedPrice = <Typography className={classes.cell}>Price by Pet</Typography>;
    const variablePrice = <Typography className={classes.cell}>Variable Price</Typography>;

    return (
        <Box className={containerStyle}>
            <Box
                className={classes.color}
                style={{ backgroundColor: props.service.color || undefined }}
            />
            <Box className={classes.categoryNamePetTypeContainer}>
                <Box className={classes.nameContainer}>
                    <Typography className={classes.name}>{props.service.name}</Typography>
                </Box>
                <Box className={classes.petTypeContainer}>{petTypes}</Box>
            </Box>
            <Box className={clsx(classes.staffContainer, classes.hideOnMobile)}>
                {props.service.status.id === Status.DRAFT ? (
                    <Button
                        label={buttonLabel}
                        className={classes.button}
                        variant="secondary"
                        onClick={clickButtonHandler}
                    />
                ) : (
                    <Typography className={classes.cell}>
                        {props.service.staff?.length > 0
                            ? `${props.service.staff?.length}${
                                  props.service.staff?.length === 1 ? ' Member' : ' Members'
                              }`
                            : '-'}
                    </Typography>
                )}
            </Box>
            <Box className={clsx(classes.durationContainer, classes.hideOnMobile)}>
                <Typography className={classes.cell}>
                    {props.service.duration.simple
                        ? durationMessage(props.service.duration.simple.hours, props.service.duration.simple.minutes)
                        : 'Variable Duration'}
                </Typography>
            </Box>
            <Box className={clsx(classes.priceContainer, classes.hideOnMobile)}>
                <Box className={clsx(classes.price, classes.cell)}>
                    {props.service.status.id !== Status.DRAFT && (
                        <Box className={classes.container}>
                            {props.service.petTypes.length === 1 ? (
                                props.service.pricing.fixedPrice.length > 0 ? (
                                    <Typography className={classes.cell}>
                                        ${props.service.pricing.fixedPrice[0].price?.toFixed(2)}
                                    </Typography>
                                ) : undefined
                            ) : (
                                props.service.pricing.fixedPrice.length > 0 && fixedPrice
                            )}
                            {props.service.pricing.variablePrice && variablePrice}
                        </Box>
                    )}
                </Box>
            </Box>
            <Box className={clsx(classes.taxesContainer, classes.hideOnMobile)}>
                <Box className={classes.cell}>
                    <Box className={classes.container}>
                        {props.service.pricing.taxes !== undefined
                            ? props.service.pricing.taxes.length === 0
                                ? noTaxes
                                : taxes
                            : noTaxes}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ServicesRowContent;
